import { useState, useRef } from "react";
import axios from "axios";
import { useAuthContext } from "../../../hooks/useAuthContext";
import { ApiConnectionString } from "../../../constants/ApiConntection";
import LoadingSpinner from "../../../components/LoadingSpinner";

export default function UploadImageToPostModal({ id, cancelModal }) {
  const [selectedFile, setSelectedFile] = useState([]); // multiple files
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [previewUrl, setPreviewUrl] = useState([]); // multiple previews
  const [isLoading, setIsLoading] = useState(false);
  const { user } = useAuthContext();

  const fileInputRef = useRef(null);

  const handleFileChange = (e) => {
    const newFiles = Array.from(e.target.files || []);
    if (newFiles.length === 0) return;

    // optional: de-dupe by file name+size+lastModified
    const key = (f) => `${f.name}-${f.size}-${f.lastModified}`;
    const existing = new Set(selectedFile.map(key));
    const unique = newFiles.filter((f) => !existing.has(key(f)));

    setSelectedFile((prev) => [...prev, ...unique]);
    setPreviewUrl((prev) => [
      ...prev,
      ...unique.map((f) => URL.createObjectURL(f)),
    ]);

    // reset input so user can select same file again if needed
    if (fileInputRef.current) fileInputRef.current.value = null;
  };

  const removeAt = (idx) => {
    setSelectedFile((prev) => prev.filter((_, i) => i !== idx));
    setPreviewUrl((prev) => prev.filter((_, i) => i !== idx));
  };

  const fileUploadHandler = async (e) => {
    e.preventDefault();
    if (!selectedFile || selectedFile.length === 0) return;

    setIsLoading(true);

    const formData = new FormData();
    selectedFile.forEach((file) => {
      formData.append("image", file);
    });

    try {
      const response = await axios.post(
        `${ApiConnectionString}/api/image/ClubPost/${id}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${user.token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );
      if (response.status === 200) {
        setUploadSuccess(true);
      }
    } catch (error) {
      console.error("Error uploading image:", error);
    } finally {
      setIsLoading(false);
      setSelectedFile([]);
      setPreviewUrl([]);
      if (fileInputRef.current) {
        fileInputRef.current.value = null;
      }
    }
  };

  if (isLoading) {
    return (
      <div className="send-modal-overlay">
        <div className="send-modal">
          <LoadingSpinner />
          <div className="send-modal-footer"></div>
        </div>
      </div>
    );
  }

  if (uploadSuccess) {
    return (
      <div className="send-modal-overlay">
        <div className="send-modal">
          <div className="blueBanner">
            <p className="fw-bold m-2 p-2 fontSize20">Images Uploaded</p>
          </div>

          <button
            type="button"
            className="close-button"
            onClick={cancelModal}
            aria-label="Close"
          >
            &times;
          </button>

          <p>Would you like to add more images?</p>
          <div className="send-modal-footer">
            <button
              className="saveChanges-button mx-3"
              onClick={() => {
                setUploadSuccess(false);
                setPreviewUrl([]);
                setSelectedFile([]);
                if (fileInputRef.current) {
                  fileInputRef.current.value = null;
                }
              }}
            >
              Yes
            </button>
            <button
              onClick={cancelModal}
              className="pointer cancelButton fw-bold mx-2 px-3"
            >
              No
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="send-modal-overlay">
      <div className="send-modal">
        <div>
          <div className="send-modal-content">
            <div className="blueBanner">
              <p className="fw-bold m-2 p-2 fontSize20">Upload Post Images</p>
            </div>

            <button
              type="button"
              className="close-button"
              onClick={cancelModal}
              aria-label="Close"
            >
              &times;
            </button>

            <div>
              <input
                type="file"
                multiple
                accept="image/*"
                onChange={handleFileChange}
                ref={fileInputRef}
              />
              <button
                className="saveChanges-button"
                onClick={fileUploadHandler}
                disabled={!selectedFile || selectedFile.length === 0}
              >
                Upload
              </button>
            </div>

            {previewUrl.length > 0 && (
              <div className="row g-2 mt-3">
                {previewUrl.map((src, idx) => (
                  <div key={idx} className="col-4">
                    <img
                      src={src}
                      alt={`Preview ${idx + 1}`}
                      className="img-thumbnail w-100"
                    />
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary w-100 mt-1"
                      onClick={() => removeAt(idx)}
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="send-modal-footer">
            <button
              onClick={cancelModal}
              className="pointer cancelButton fw-bold mx-2 px-3"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
